/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { RecipientInfo, SenderInfo } from '../types';
import useSEO from '../hooks/useSEO';
import { Button, Field, FlowPage, Icon } from '../components/bx';
import { flowSteps } from './ItemsPage';
import { validateContact } from './SenderPage';
import '../styles/bx.css';

interface RecipientPageProps {
  data: RecipientInfo;
  senderInfo: SenderInfo;
  isGetMe?: boolean;
  isSubscribe?: boolean;
  onChange: (data: RecipientInfo) => void;
  onContinue: () => void;
  onBack: () => void;
}

type Errors = Partial<Record<'email' | 'fullName' | 'phone' | 'address' | 'city' | 'state', string>>;

export default function RecipientPage({ data, senderInfo, isGetMe, onChange, onContinue, onBack }: RecipientPageProps) {
  const [errors, setErrors] = useState<Errors>({});

  useSEO({ title: isGetMe ? 'Delivery address - BOQBOX' : 'Recipient details - BOQBOX', description: 'Where should BOQBOX deliver?' });

  // "Get me" orders are delivered to the person placing them.
  useEffect(() => {
    if (isGetMe) onChange({ ...data, email: senderInfo.email, fullName: senderInfo.fullName, phone: senderInfo.phone });
  }, [isGetMe]);

  const validate = (): Errors => {
    const next: Errors = isGetMe ? {} : validateContact(data);
    if (!data.address.trim()) next.address = 'Enter the street address';
    if (!data.city.trim()) next.city = 'Enter the city';
    if (!data.state.trim()) next.state = 'Enter the state';
    return next;
  };

  const handleContinue = () => {
    const next = validate();
    setErrors(next);
    if (!Object.keys(next).length) onContinue();
  };
  const set = (key: keyof RecipientInfo, value: string) => {
    onChange({ ...data, [key]: value });
    if (errors[key as keyof Errors]) setErrors({ ...errors, [key]: undefined });
  };

  return (
    <FlowPage
      eyebrow={isGetMe ? 'Get me' : 'Send a gift'}
      title={isGetMe ? 'Where should we deliver?' : 'Who’s it going to?'}
      subtitle={isGetMe ? 'Your order arrives in a plain, unlabelled bag.' : 'We deliver discreetly in a plain bag and let them know it’s from you.'}
      steps={flowSteps(isGetMe)}
      step={3}
      onBack={onBack}
      narrow
      bar={
        <>
          <div className="bx-bar-copy"><small>Next</small><strong>Review & pay</strong></div>
          <Button onClick={handleContinue}>Continue <Icon name="arrow" /></Button>
        </>
      }
    >
      <form className="bx-stack" onSubmit={(e) => { e.preventDefault(); handleContinue(); }} noValidate>
        {isGetMe ? (
          <div className="bx-card bx-card-pad bx-row" style={{ alignItems: 'center' }}>
            <span className="bx-option-icon" aria-hidden="true"><Icon name="user" /></span>
            <div style={{ minWidth: 0 }}>
              <strong style={{ display: 'block' }}>Delivering to you</strong>
              <span className="bx-muted" style={{ fontSize: 14 }}>{[senderInfo.fullName, senderInfo.phone].filter(Boolean).join(' · ')}</span>
            </div>
          </div>
        ) : (
          <div className="bx-card bx-card-pad">
            <h2>Recipient</h2>
            <p className="bx-sub">We’ll call this number when we arrive.</p>
            <div className="bx-form two">
              <Field label="Full name" error={errors.fullName} span>
                <input className="bx-input" autoComplete="off" placeholder="Their name" value={data.fullName} onChange={(e) => set('fullName', e.target.value)} />
              </Field>
              <Field label="Phone number" error={errors.phone}>
                <input className="bx-input" type="tel" inputMode="tel" placeholder="0805 987 6543" value={data.phone} onChange={(e) => set('phone', e.target.value)} />
              </Field>
              <Field label="Email" error={errors.email} hint="We’ll let them know a gift is coming.">
                <input className="bx-input" type="email" inputMode="email" placeholder="them@example.com" value={data.email} onChange={(e) => set('email', e.target.value)} />
              </Field>
            </div>
          </div>
        )}

        <div className="bx-card bx-card-pad">
          <h2>Delivery address</h2>
          <p className="bx-sub">Include a landmark if the place is hard to find.</p>
          <div className="bx-form two">
            <Field label="Street address" error={errors.address} span>
              <input className="bx-input" autoComplete="street-address" placeholder="14 Adeola Odeku Street, Victoria Island" value={data.address} onChange={(e) => set('address', e.target.value)} />
            </Field>
            <Field label="City" error={errors.city}>
              <input className="bx-input" autoComplete="address-level2" placeholder="Lagos" value={data.city} onChange={(e) => set('city', e.target.value)} />
            </Field>
            <Field label="State" error={errors.state}>
              <input className="bx-input" autoComplete="address-level1" placeholder="Lagos" value={data.state} onChange={(e) => set('state', e.target.value)} />
            </Field>
          </div>
        </div>

        {!isGetMe ? (
          <div className="bx-card bx-card-pad">
            <Field label="Gift note" optional hint="We’ll include this with the delivery.">
              <textarea className="bx-input" rows={3} maxLength={500} placeholder="Happy birthday! Enjoy your week ❤️" value={data.message || ''} onChange={(e) => set('message', e.target.value)} />
            </Field>
          </div>
        ) : null}
        <button type="submit" hidden />
      </form>
    </FlowPage>
  );
}
